import { useMemo } from 'react';
import { MapSurface } from '../../../map/MapSurface';
import {
  deriveInsetView,
  insetOverlayScene,
  insetPresentationScale,
  projectInsetFootprint,
  type GroundFootprint,
} from '../../../composition/inset';
import { compositionAnnotationRenderScale } from '../../../composition/scale';
import { useAppStore } from '../../state/useAppStore';

const ignoreView = () => {};

export function InsetMap({
  footprint,
  mainZoom,
  width,
  height,
  pixelsPerMm,
}: {
  readonly footprint: GroundFootprint | null;
  readonly mainZoom: number;
  readonly width: number;
  readonly height: number;
  readonly pixelsPerMm: number;
}) {
  const project = useAppStore((state) => state.project);
  const inset = project.composition.insetMap!;
  const { hiddenLayerIds, hiddenAnnotationLayerIds, hiddenAnnotationIds } = inset;
  const scene = useMemo(
    () =>
      insetOverlayScene(
        {
          sources: project.sources,
          layers: project.layers,
          annotationLayers: project.annotationLayers,
        },
        { hiddenLayerIds, hiddenAnnotationLayerIds, hiddenAnnotationIds },
      ),
    [
      project.sources,
      project.layers,
      project.annotationLayers,
      hiddenLayerIds,
      hiddenAnnotationLayerIds,
      hiddenAnnotationIds,
    ],
  );
  const view = footprint ? deriveInsetView(footprint, width, height, inset.contextMargin) : null;
  if (!view || !footprint) return <span role="status">Inset map extent unavailable</span>;
  const scale = insetPresentationScale(mainZoom, view.zoom);
  const points = projectInsetFootprint(footprint, view, width, height);
  return (
    <div
      style={{
        width,
        height,
        pointerEvents: 'none',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: inset.cornerRadiusMm * pixelsPerMm,
      }}
      data-inset-zoom={view.zoom}
    >
      <MapSurface
        basemap={project.map.basemap}
        backgroundColour={project.map.backgroundColour}
        view={view}
        sources={project.sources}
        symbolAssets={project.symbolAssets}
        layers={scene.layers}
        annotationLayers={scene.annotationLayers ?? []}
        presentationScale={scale}
        activeFocusAreaId={project.activeFocusAreaId}
        annotationRenderScale={compositionAnnotationRenderScale(pixelsPerMm)}
        onViewChange={ignoreView}
        interactive={false}
        renderWorldCopies
        minimumZoom={-10}
        maximumZoom={24}
        showAttribution={false}
      />
      <svg
        aria-label="Main map footprint"
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0 }}
      >
        <polygon
          points={points.map((p) => `${p.x},${p.y}`).join(' ')}
          fill={inset.indicator.fillColour}
          fillOpacity={inset.indicator.fillOpacity / 100}
          stroke={inset.indicator.outlineColour}
          strokeWidth={inset.indicator.outlineWidthMm * pixelsPerMm}
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
